import {Component, Input, OnInit} from '@angular/core';
import {EmpresaService} from '../../services/empresa.service';

type AcaoIntegracaoIfood = 'DESATIVAR' | 'REMOVER';

@Component({
  selector: 'app-tela-integracao-ifood',
  templateUrl: './tela-integracao-ifood.component.html',
  styleUrls: ['./tela-integracao-ifood.component.scss']
})
export class TelaIntegracaoIfoodComponent implements OnInit {
  @Input() empresa: any = {};
  integracaoEmEdicao: any = null;
  confirmacao: {acao: AcaoIntegracaoIfood; integracao: any} = null;
  sincronizando = false;
  processandoIntegracaoId: number = null;
  merchantIdCopiado: number = null;
  mensagemErro: string;
  mensagemRestauracao: string;
  restauracaoPendente = false;
  renovandoTokenId: number = null;
  errosToken: {[integracaoId: number]: string} = {};

  constructor(private empresaService: EmpresaService) { }

  ngOnInit(): void {
    if(!Array.isArray(this.empresa.integracoesIfood)) this.empresa.integracoesIfood = [];

    this.empresaService.obtenhaAtivacaoPendente(this.empresa).then((ifooodUserCode: any) => {
      if(!ifooodUserCode) return;
      this.novaIntegracaoIfood();
      this.integracaoEmEdicao.ifooodUserCode = ifooodUserCode;
    }).catch((erro: any) => this.mensagemErro = this.textoErro(erro));
  }

  get integracoes(): any[] {
    return (this.empresa.integracoesIfood || []).filter((integracao: any) => !integracao.removida);
  }

  get quantidadeLojas(): number {
    return this.integracoes.length;
  }

  get quantidadeCatalogApiAtiva(): number {
    return this.integracoes.filter((integracao: any) => this.catalogApiAtiva(integracao)).length;
  }

  catalogApiAtiva(integracao: any): boolean {
    return integracao != null && integracao.desativado !== true;
  }

  get quantidadeOrderApiAtiva(): number {
    return this.integracoes.filter((integracao: any) =>
      !integracao.desativado && integracao.orderApi === true).length;
  }

  get quantidadeShippingApiAtiva(): number {
    return this.integracoes.filter((integracao: any) =>
      !integracao.desativado && integracao.shippingApi === true).length;
  }

  novaIntegracaoIfood(): void {
    this.mensagemErro = null;
    this.mensagemRestauracao = null;
    this.integracaoEmEdicao = {
      nova: true,
      orderApi: false,
      shippingApi: false,
      publicacaoCatalogoHabilitada: false
    };
  }

  editeIntegracao(integracao: any): void {
    this.mensagemErro = null;
    this.mensagemRestauracao = null;
    this.integracaoEmEdicao = {...integracao, editar: true};
  }

  canceleEdicao(): void {
    this.integracaoEmEdicao = null;
  }

  concluaEdicao(integracaoSalva: any): void {
    const restauracaoCanais = integracaoSalva?.restauracaoCanais;
    const dadosIntegracao = {...integracaoSalva};
    delete dadosIntegracao.restauracaoCanais;
    const integracoes = this.empresa.integracoesIfood || [];
    const existente = integracoes.find((integracao: any) => integracao.id === dadosIntegracao.id);

    if(existente)
      Object.assign(existente, dadosIntegracao, {editar: false});
    else
      integracoes.push({...dadosIntegracao, editar: false});

    this.empresa.integracoesIfood = integracoes;
    this.integracaoEmEdicao = null;
    this.apresenteRestauracao(restauracaoCanais);
  }

  integracaoCentralizada(): boolean {
    return !!this.integracaoEmEdicao?.id && !this.integracaoEmEdicao.codigoAutorizacao;
  }

  async sincronizarFormasPagamentos(): Promise<void> {
    this.sincronizando = true;
    this.mensagemErro = null;
    try {
      await this.empresaService.sincronizeFormasPagamentoIfood();
    } catch(erro) {
      this.mensagemErro = this.textoErro(erro);
    } finally {
      this.sincronizando = false;
    }
  }

  soliciteConfirmacao(acao: AcaoIntegracaoIfood, integracao: any): void {
    this.confirmacao = {acao, integracao};
  }

  canceleConfirmacao(): void {
    if(this.processandoIntegracaoId != null) return;
    this.confirmacao = null;
  }

  async confirmeAcao(): Promise<void> {
    if(!this.confirmacao || this.processandoIntegracaoId != null) return;

    const {acao, integracao} = this.confirmacao;
    this.processandoIntegracaoId = integracao.id;
    this.mensagemErro = null;
    try {
      if(acao === 'DESATIVAR') {
        await this.empresaService.desativeIntegracaoIfood(integracao);
        integracao.desativado = true;
        integracao.publicacaoCatalogoHabilitada = false;
      } else {
        await this.empresaService.removaIntegracaoIfood(integracao);
        this.empresa.integracoesIfood = this.integracoes.filter((item: any) => item.id !== integracao.id);
      }
      this.confirmacao = null;
    } catch(erro) {
      this.mensagemErro = this.textoErro(erro);
      this.confirmacao = null;
    } finally {
      this.processandoIntegracaoId = null;
    }
  }

  async ativeIntegracao(integracao: any): Promise<void> {
    if(this.processandoIntegracaoId != null) return;
    this.processandoIntegracaoId = integracao.id;
    this.mensagemErro = null;
    try {
      await this.empresaService.ativeIntegracaoIfood(integracao);
      integracao.desativado = false;
    } catch(erro) {
      this.mensagemErro = this.textoErro(erro);
    } finally {
      this.processandoIntegracaoId = null;
    }
  }

  async copieMerchantId(integracao: any): Promise<void> {
    try {
      await navigator.clipboard.writeText(String(integracao.idLoja || ''));
      this.merchantIdCopiado = integracao.id;
      setTimeout(() => {
        if(this.merchantIdCopiado === integracao.id) this.merchantIdCopiado = null;
      }, 1800);
    } catch(erro) {
      this.mensagemErro = 'Não foi possível copiar o Merchant ID.';
    }
  }

  tokenVencido(integracao: any): boolean {
    if(!integracao.validadeToken) return false;
    const validade = new Date(integracao.validadeToken).getTime();
    return Number.isFinite(validade) && validade <= Date.now();
  }

  async renoveToken(integracao: any): Promise<void> {
    if(!integracao?.id || this.processandoIntegracaoId != null) return;
    this.processandoIntegracaoId = integracao.id;
    this.renovandoTokenId = integracao.id;
    delete this.errosToken[integracao.id];
    try {
      const resultado = await this.empresaService.renoveTokenIfood(integracao.id);
      integracao.validadeToken = resultado.validadeToken;
    } catch(erro) {
      this.errosToken[integracao.id] = this.textoErro(erro);
    } finally {
      this.processandoIntegracaoId = null;
      this.renovandoTokenId = null;
    }
  }

  descricaoIntegracao(integracao: any): string {
    if(integracao.clienteId) return 'Meu Cardápio Catálogo distribuída';
    if(!integracao.codigoAutorizacao) return 'Integração centralizada';
    if(integracao.orderApi && integracao.shippingApi) return 'Meu Cardápio Completo';
    if(integracao.shippingApi) return 'Meu Cardápio Delivery';
    if(integracao.orderApi) return 'Meu Cardápio Pedidos';
    return 'Meu Cardápio Catálogo distribuída';
  }

  tituloConfirmacao(): string {
    return this.confirmacao?.acao === 'REMOVER' ? 'Remover integração iFood' : 'Desativar integração iFood';
  }

  mensagemConfirmacao(): string {
    const nome = this.confirmacao?.integracao?.nomeLoja || this.confirmacao?.integracao?.idLoja || 'selecionada';
    return this.confirmacao?.acao === 'REMOVER'
      ? `A integração da loja ${nome} será removida permanentemente.`
      : `A integração da loja ${nome} será desativada e deixará de processar seus serviços.`;
  }

  textoBotaoConfirmacao(): string {
    return this.confirmacao?.acao === 'REMOVER' ? 'Remover' : 'Desativar';
  }

  private textoErro(erro: any): string {
    return String(erro?.message || erro || 'Não foi possível concluir a operação.');
  }

  private apresenteRestauracao(restauracao: any): void {
    this.mensagemRestauracao = null;
    this.restauracaoPendente = false;
    if(!restauracao) return;

    const mensagens: string[] = [];
    if(restauracao.reativados)
      mensagens.push(this.quantifique(restauracao.reativados, 'canal reativado', 'canais reativados'));
    if(restauracao.religadosInativos)
      mensagens.push(this.quantifique(restauracao.religadosInativos,
        'canal religado e mantido inativo', 'canais religados e mantidos inativos'));
    if(restauracao.pendentesPublicacao)
      mensagens.push(this.quantifique(restauracao.pendentesPublicacao,
        'canal aguardando habilitar a publicação de catálogo',
        'canais aguardando habilitar a publicação de catálogo'));
    if(restauracao.pendentesCatalogo)
      mensagens.push(this.quantifique(restauracao.pendentesCatalogo,
        'canal com catálogo não encontrado no iFood',
        'canais com catálogo não encontrado no iFood'));
    if(restauracao.pendentesValidacao)
      mensagens.push(this.quantifique(restauracao.pendentesValidacao,
        'canal aguardando nova validação dos catálogos',
        'canais aguardando nova validação dos catálogos'));

    this.restauracaoPendente = !!(restauracao.pendentesPublicacao ||
      restauracao.pendentesCatalogo || restauracao.pendentesValidacao);
    this.mensagemRestauracao = mensagens.map(mensagem => `${mensagem}.`).join(' ') || null;
  }

  private quantifique(quantidade: number, singular: string, plural: string): string {
    return `${quantidade} ${quantidade === 1 ? singular : plural}`;
  }
}
